<template>
  <div>
    <global-confirm-box ref="confirmBox"></global-confirm-box>

    <page-header
      :title="$t('meal_list')"
      :subtitle="$t('manage_meals')"
      icon="mdi-food"
      icon-color="primary"
    >
      <template #actions v-if="!device.isMobile">
        <primary-button
          v-if="$store.state.restaurant.parent"
          @click="isCloneDialogOpen = true"
          class="mr-2"
        >
          {{ $t("cloneM") }}
          {{ toLatin($store.state.restaurant?.parent?.name || "") }}
        </primary-button>

        <primary-button class="mr-2" @click="isSizesDialogOpen = true">
          {{ $t("meal_size1") }}
        </primary-button>

        <global-button-print @click="printMeals" :disabled="!meals.length" />
        <global-button-excel @click="exportMeals" :disabled="!meals.length" />

        <primary-button @click="isMealDialogOpen = true">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_meals") }}
        </primary-button>
      </template>
    </page-header>

    <page-toolbar :loading="isLoading" :searchable="false">
      <template #filters>
        <v-col cols="auto">
          <autocomplete
            v-model="selectedCategoryIds"
            :items="mealCategories"
            multiple
            :label="$t('group_meal1')"
            return-object
            item-text="name"
            item-value="id"
            @change="filterMealsByCategory()"
            hide-details
            color="white"
            style="width: 300px"
          />
        </v-col>
        <v-col cols="auto">
          <text-field
            v-model="searchQuery"
            kind="search"
            hide-details
            color="white"
            :label="$t('search_meals_placeholder')"
          />
        </v-col>

        <v-col>
          <v-row no-gutters justify="end" align="center">
            <v-btn-toggle v-model="currentViewLayout" mandatory dense class="mr-2">
              <v-btn small value="table">
                <v-icon small>mdi-table</v-icon>
                <span class="ml-1">{{ $t("tableLayout") }}</span>
              </v-btn>
              <v-btn small value="cards">
                <v-icon small>mdi-view-grid</v-icon>
                <span class="ml-1">{{ $t("cards") }}</span>
              </v-btn>
            </v-btn-toggle>

            <circle-button
              @click="
                $router.push({
                  name: 'foods-qrlist',
                  params: { jela: $store.state.restaurant.id },
                })
              "
              icon="mdi-qrcode"
              :small="false"
              :tooltip="$t('tooltip_meal_qr_codes')"
            ></circle-button>
          </v-row>
        </v-col>
      </template>
    </page-toolbar>

    <global-dialog-card
      v-model="isCloneDialogOpen"
      :title="$t('cloneM')"
      max-width="1200"
      :subtitle="$store.state.restaurant?.parent?.name || ''"
    >
      <text-field
        v-model="cloneSearchQuery"
        prepend-inner-icon="mdi-magnify"
        :label="$t('search')"
        hide-details
      />
      <v-data-table
        dense
        :headers="cloneTableHeaders"
        :search="cloneSearchQuery"
        class="ordera-box-shadow striped-table"
        style="height: 100%"
        :items="parentRestaurantMeals"
      >
        <template
          v-for="header in cloneTableHeaders"
          v-slot:[`header.${header.value}`]="{ header }"
        >
          <span v-if="header.text == 'mark'">
            <input
              id="checkbox_id"
              type="checkbox"
              v-model="isSelectAllChecked"
              @change.prevent="toggleSelectAll()"
            />
            <label for="checkbox_id">{{ $t("mark") }}</label>
          </span>
          <span v-else>{{ $t(header.text) }}</span>
        </template>
        <template v-slot:item.actions="{ item, index }">
          <span v-if="!clonedMealsMap[item.id]">
            <input type="checkbox" v-model="item.selected" />
          </span>
          <span v-else style="font-size: 13px; color: rgba(0, 0, 0, 0.4)">
            {{ $t("cloned") }}
          </span>
        </template>
      </v-data-table>

      <template #actions>
        <v-btn text @click="isCloneDialogOpen = false">
          {{ $t("cancel") }}
        </v-btn>
        <primary-button @click="cloneMealsFromParent">
          {{ $t("clone") }}
        </primary-button>
      </template>
    </global-dialog-card>
    <food-dialog
      v-model="isMealDialogOpen"
      :edited-item="currentMeal"
      @save="saveMeal"
      @getSizes="loadMealSizes"
      :categories="translatedMealCategories"
      :additional-food-new="additionalFoodCategories"
      :side-dishes="sideDishes"
    ></food-dialog>

    <food-portion-dialog
      v-model="isSizesDialogOpen"
      :sizes="mealSizes"
    ></food-portion-dialog>

    <div class="px-6 mt-2">
      <!-- Table Layout -->
      <meals-table-view
        v-if="currentViewLayout === 'table'"
        :meals="meals"
        :search="searchQuery"
        :is-loading="isLoading"
      />

      <!-- Cards Layout -->
      <meals-cards-view
        v-else-if="currentViewLayout === 'cards'"
        :meals="meals"
        :search="searchQuery"
      />
    </div>

    <food-qr-dialog v-model="isQrDialogOpen" :food="selectedMealForQr"></food-qr-dialog>

    <!-- Mobile FAB Menu -->
    <v-speed-dial
      v-if="device.isMobile"
      v-model="fabMenu"
      fixed
      right
      direction="bottom"
      transition="slide-y-reverse-transition"
      style="top: 80px"
    >
      <template v-slot:activator>
        <v-btn v-model="fabMenu" color="grey" fab dark small depressed>
          <v-icon v-if="fabMenu">mdi-close</v-icon>
          <v-icon v-else>mdi-dots-vertical</v-icon>
        </v-btn>
      </template>

      <!-- Excel Button -->
      <v-btn
        fab
        small
        color="green darken-2"
        dark
        @click="exportMeals"
        :disabled="!meals.length"
      >
        <v-icon>mdi-microsoft-excel</v-icon>
      </v-btn>

      <!-- Print Button -->
      <v-btn fab small color="primary" dark @click="printMeals" :disabled="!meals.length">
        <v-icon>mdi-printer</v-icon>
      </v-btn>

      <!-- Refresh Button -->
      <v-btn fab small color="blue" dark @click="loadMeals">
        <v-icon>mdi-reload</v-icon>
      </v-btn>
    </v-speed-dial>
  </div>
</template>

<script>
import conf from "@/config";
import { defaultFood } from "@/models/food";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasSnackbar from "@/mixins/hasSnackbar";
import mealActions from "@/mixins/mealActions";
import FoodQrDialog from "@/components/food/fields/dialogs/FoodQrDialog.vue";
import FoodDialog from "@/components/food/fields/dialogs/FoodDialog.vue";
import FoodPortionDialog from "@/components/food/fields/dialogs/FoodPortionDialog.vue";
import PageHeader from "@/components/global/PageHeader.vue";
import PageToolbar from "@/components/global/PageToolbar.vue";
import GlobalButtonExcel from "@/components/global/buttons/GlobalButtonExcel.vue";
import GlobalButtonPrint from "@/components/global/buttons/GlobalButtonPrint.vue";
import MealsTableView from "@/components/food/MealsTableView.vue";
import MealsCardsView from "@/components/food/MealsCardsView.vue";
import categoryService from "@/services/category";
import mealService from "@/services/meal";

export default {
  mixins: [hasPrinting, hasExcel, hasSnackbar, mealActions],
  components: {
    FoodQrDialog,
    FoodDialog,
    FoodPortionDialog,
    PageHeader,
    PageToolbar,
    GlobalButtonExcel,
    GlobalButtonPrint,
    MealsTableView,
    MealsCardsView,
  },
  data: () => ({
    fabMenu: false,
    selectedCategoryIds: "",
    conf: conf,
    searchQuery: "",
    cloneSearchQuery: "",
    selectedMealForQr: {},
    isMealDialogOpen: false,
    isSizesDialogOpen: false,
    isEditSizesDialogOpen: false,
    isQrDialogOpen: false,
    isCloneDialogOpen: false,
    sideDishes: [],
    isSelectAllChecked: false,
    meals: [],
    allMeals: [],
    additionalFoodCategories: [],
    currentMeal: null,
    mealCategories: [],
    mealSizes: [],
    editedSize: {},
    newPrice: "",
    parentRestaurantMeals: "",
    isLoading: false,
    currentViewLayout: "table",
  }),

  computed: {
    availableLanguages() {
      // Filter languages based on restaurant settings (same logic as LanguageSelector)
      const restaurant =
        this.$store.state.restaurant || this.$store.state.company?.restaurant;
      const restaurantLanguages = restaurant?.settings?.language || [];

      const def = [{
        code: "default",
        text: "default",
        locale: "default",
        flag: "🇷",
        script: "default"
      }];

      if (restaurantLanguages.length === 0) {
        // If no restaurant languages configured, return all available languages
        let data =  this.getAvailableLanguages()
        return def.concat(data);
      }

      // Filter available languages to only include those configured for the restaurant
      let data =  this.getAvailableLanguages().filter((lang) =>
        restaurantLanguages.includes(lang.code)
      )
      return def.concat(data);
    },
    translatedMealCategories() {
      return this.mealCategories.map((category) => {
        return {
          ...category,
          name: this.toLatin(category.name),
        };
      });
    },
    clonedMealsMap() {
      const clonedMap = {};
      this.meals.forEach((meal) => {
        clonedMap[meal.clone_food_id] = !!meal.clone_food_id;
      });
      return clonedMap;
    },
    cloneTableHeaders() {
      return [
        {
          text: this.$t("mark"),
          align: "center",
          value: "actions",
          sortable: false,
        },
        { text: this.$t("title"), align: "start", value: "name" },
        { text: this.$t("id"), align: "start", value: "id" },
      ];
    },
  },
  created() {
    this.currentMeal = this.getDefaultFood()
    if (this.$store.state.restaurant.parent_id) {
      this.loadParentRestaurantMeals();
    }
    this.loadMeals();
    this.loadMealCategories();
    this.loadMealSizes();
  },

  methods: {
    toggleSelectAll() {
      const isSelected = this.isSelectAllChecked;
      this.parentRestaurantMeals.forEach((meal) => {
        meal.selected = isSelected;
      });
    },
    cloneMealsFromParent() {
      const restaurantId = this.$store.state.restaurant.id;
      const selectedMeals = this.parentRestaurantMeals
        .filter((meal) => meal.selected)
        .map((meal) => ({ ...meal, restoran: restaurantId }));

      const payload = {
        items: selectedMeals,
        restoran: restaurantId,
      };

      mealService
        .cloneParentFoods(payload)
        .then(({ data }) => {
          if (data) {
            this.loadMeals();
            this.loadMealCategories();
            this.loadMealSizes();
            this.isCloneDialogOpen = false;
            this.cloneSearchQuery = "";
            this.$forceUpdate();
            this.showSuccessSnackbar(this.$t("meals_cloned"));
          }
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("error_cloning_meals"));
        });
    },
    async loadParentRestaurantMeals() {
      try {
        const { data } = await mealService.getParentFoods(
          this.$store.state.restaurant.parent_id
        );
        this.parentRestaurantMeals = data.map((meal) => ({
          ...meal,
          selected: false,
        }));
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_parent_meals"));
      }
    },
    applyMealFilters() {
      if (this.selectedCategoryIds.length === 0) {
        this.meals = [...this.allMeals];
        return;
      }

      const categoryIds = this.selectedCategoryIds.map((cat) => cat.id);
      this.meals = this.allMeals.filter((meal) => categoryIds.includes(meal.category_id));
    },
    filterMealsByCategory() {
      this.applyMealFilters();
    },

    async loadMealCategories() {
      try {
        const res = await categoryService.getCategories();
        this.mealCategories = res.data;
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_categories"));
      }
    },
    async loadMealSizes() {
      try {
        const res = await mealService.getFoodSizes();
        this.mealSizes = res.data;
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_sizes"));
      }
    },
    async loadMeals() {
      this.isLoading = true;
      try {
        const res = await mealService.getFoods();
        this.allMeals = res.data;
        this.sideDishes = res.data.filter((meal) => meal.category?.prilog);
        this.applyMealFilters();
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_meals"));
      } finally {
        this.isLoading = false;
      }
    },
    editMeal(item) {

      const existingTranslations = item.translations || [];
      const mergedTranslations = this.availableLanguages.map((lang) => {
        const found = existingTranslations.find((t) => t.code === lang.code);
        return found ? { ...found } : { code: lang.code, name: '', description: '' };
      });
      item.translations = mergedTranslations;

      // Prepare additional food categories
      this.additionalFoodCategories = this.mealCategories
        .filter((cat) => cat.main_group != 1)
        .map((cat) => {
          const matchingFood = item.additional_food?.find(
            (food) => food.category_id == cat.id
          );
          return {
            ...cat,
            active: matchingFood ? 1 : 0,
            choice_number: matchingFood?.choice_number || "",
          };
        });

      // Prepare menu types
      const menuTypeIds = item.menu_types?.map((menu) => menu.menu_type_id) || [];

      // Prepare selected prices
      const selectedPrices = [];
      this.mealSizes.forEach((size) => {
        size.is_true = false;
        size.price_meal = "";

        const priceMatch = size.price?.find((p) => p.meal_id == item.id);
        if (priceMatch) {
          size.is_true = true;
          size.price_meal = priceMatch.price || "";
          size.price_meal_id = priceMatch.id || "";
          selectedPrices.push({
            name: size.name,
            id: priceMatch.size_id,
            price: priceMatch.price,
            meal_id: priceMatch.meal_id,
            size_id: priceMatch.size_id,
            restaurant_id: priceMatch.restaurant_id,
          });
        }
      });

      // Set current meal with all prepared data
      this.currentMeal = {
        ...item,
        type: menuTypeIds,
        sizes: this.mealSizes,
        selectedPrices,
      };

      this.isMealDialogOpen = true;
    },
    async deleteMeal(item) {
      try {
        const confirmed = await this.$refs.confirmBox.open(
          this.$t("delete_confirmation"),
          ``,
          { color: "error" }
        );

        if (confirmed) {
          await mealService.deleteFood(item.id);

          // Remove from allMeals array by ID
          this.allMeals = this.allMeals.filter((m) => m.id !== item.id);
          this.sideDishes = this.sideDishes.filter((m) => m.id !== item.id);
          this.applyMealFilters();

          this.showSuccessSnackbar(this.$t("meal_deleted"));
        }
      } catch (error) {
        if (error) {
          this.showErrorSnackbar(this.$t("error_deleting_meal"));
        }
      }
    },
    async saveMeal() {
      this.currentMeal.category_id = this.currentMeal.category.id;
      this.currentMeal.sizes_id = this.currentMeal.sizes.id;

      try {
        if (this.currentMeal.id) {
          // Update existing meal
          await mealService.updateFood(this.currentMeal.id, this.currentMeal);

          // Find and update meal in allMeals array by ID
          const mealIndex = this.allMeals.findIndex((m) => m.id === this.currentMeal.id);
          if (mealIndex !== -1) {
            this.allMeals.splice(mealIndex, 1, { ...this.currentMeal });
            this.applyMealFilters();
          }

          this.showSuccessSnackbar(this.$t("meal_updated_successfully"));
        } else {
          // Create new meal
          const { data } = await mealService.createFood(this.currentMeal);

          const newMeal = {
            ...this.currentMeal,
            id: data.data.id,
            prilog: data.data.prilog,
            menu_types: data.data.menu_types,
            additional_food: [],
          };

          this.allMeals.push(newMeal);
          this.applyMealFilters();

          if (newMeal.prilog == 1) {
            this.sideDishes.push(newMeal);
          }

          this.showSuccessSnackbar(this.$t("meal_created"));
        }

        this.isMealDialogOpen = false;
      } catch (error) {
        this.showErrorSnackbar(
          this.currentMeal.id
            ? this.$t("error_updating_meal")
            : this.$t("error_creating_meal")
        );
      }
    },
    printMeals() {
      this.setPrinter();

      const headers = [
        { text: this.$t("id"), value: "id", print: true },
        { text: this.$t("image"), value: "image", print: true, isImage: true },
        { text: this.$t("title"), value: "name", print: true },
        { text: this.$t("group_meal"), value: "category_name", print: true },
        { text: this.$t("price"), value: "price", print: true },
      ];

      const items = this.customSearch(this.meals, headers, this.searchQuery).map(
        (meal) => ({
          id: meal.id,
          image: meal.image_id
            ? `${this.conf.apiUrlBase}/gallery/${
                this.$store.state.restaurant.id
              }/medium/${meal.image?.name || meal.image_id}`
            : "",
          name: meal.name,
          category_name: meal.category?.name || "",
          price: meal.price
            ? `${meal.price} ${this.$store.state.restaurant.currency || ""}`
            : "",
        })
      );

      this.printDocument({
        pageTitle: this.$t("meal_list") || "Lista jela",
        title: this.$t("meal_list") || "Lista jela",
        subtitle: this.$t("manage_meals") || "Upravljajte jelima",
        tables: [
          {
            headers,
            items,
          },
        ],
      });
    },
    exportMeals() {
      this.setExcel();

      const headers = [
        { text: this.$t("id"), value: "id", print: true },
        { text: this.$t("title"), value: "name", print: true },
        { text: this.$t("group_meal"), value: "category_name", print: true },
        { text: this.$t("price"), value: "price", print: true },
      ];

      const items = this.customSearch(this.meals, headers, this.searchQuery).map(
        (meal) => ({
          id: meal.id,
          name: meal.name,
          category_name: meal.category?.name || "",
          price: meal.price
            ? `${meal.price} ${this.$store.state.restaurant.currency || ""}`
            : "",
        })
      );

      const fileName = `jela_${new Date().toISOString().substr(0, 10)}.xlsx`;

      this.downloadExcel({
        fileName,
        sheets: [
          {
            name: this.$t("meal_list") || "Jela",
            title: this.$t("meal_list") || "Lista jela",
            subtitle: this.$t("manage_meals") || "Upravljajte jelima",
            headers,
            items,
          },
        ],
      });
    },
    getDefaultFood() {
      return {
        ...defaultFood,
        translations: this.availableLanguages.map((lang) => ({
          code: lang.code,
          name: "",
          description: "",
        })),
      };
    },
  },
  watch: {
    isMealDialogOpen(isOpen) {
      if (!isOpen) {
        this.$nextTick(() => {
          this.currentMeal = this.getDefaultFood();
        });
      }
    },
  }
};
</script>
